import { Plus } from 'lucide-react';
import { useState } from 'react';
import { BottomSheetTextButton } from '@/components/mobile';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import usePersistentOpen from '@/hooks/usePersistentOpen';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePhoneMask } from '@/hooks/usePhoneMask';
import AddToReminderSequence from '@/components/reminder-sequences/AddToReminderSequence';
import CustomerReminderSequences from '@/components/reminder-sequences/CustomerReminderSequences';
import { useGetEnrollmentsQuery } from '@/slices/reminderSequencesApi';
import {
	PHONE_DIALOG_CLASS,
	PHONE_DIALOG_HEADER_CLASS,
	PHONE_DIALOG_TITLE_CLASS,
} from '../../teams/phoneDialog';
import { DetailsSectionActions, DetailsSectionHeader } from './DetailsSection';

interface ReminderSequencesProps {
	_customer?: { _id: string; name?: string } | null;
}

/**
 * Where this customer is in each of their reminder sequences: done steps with
 * who and when, open steps with their due dates. `+` adds them to one.
 */
const ReminderSequences = ({ _customer }: ReminderSequencesProps) => {
	const customerId = _customer?._id;
	const isMobile = useIsMobile();
	const maskPhone = usePhoneMask();
	const [isOpen, setIsOpen] = usePersistentOpen('conversation-details:reminder-sequences');
	const [adding, setAdding] = useState(false);
	const { data } = useGetEnrollmentsQuery(
		{ _customer: customerId, status: 'active' },
		{ skip: !customerId },
	);
	const activeCount = data?.enrollments.length ?? 0;
	const firstName = maskPhone(_customer?.name?.split(' ')[0] || 'this customer');

	if (!customerId) return null;

	return (
		<div className="border-b border-border/60">
			<Collapsible open={isOpen} onOpenChange={setIsOpen}>
				<DetailsSectionHeader
					className="shrink-0 flex-row space-y-0"
					title={activeCount ? `Reminder sequences (${activeCount})` : 'Reminder sequences'}
					actions={
						<DetailsSectionActions>
							<Button
								variant="ghost"
								size="icon"
								onClick={() => setAdding(true)}
								className="h-7 w-7 max-md:size-11 max-md:rounded-full"
								aria-label="Add to reminder sequence"
							>
								<Plus className="h-4 w-4 max-md:size-5" />
							</Button>
						</DetailsSectionActions>
					}
				/>
				<CollapsibleContent>
					{isOpen && (
						<CustomerReminderSequences
							customerId={customerId}
							customerName={_customer?.name}
							emptyHint={
								<div className="pt-1">
									<p className="text-sm font-medium text-muted-foreground">Not on a reminder sequence</p>
									<p className="mt-0.5 text-xs text-muted-foreground/80">
										Add {firstName} to a follow-up plan so every step of their journey lands on your Today list.
									</p>
								</div>
							}
						/>
					)}
				</CollapsibleContent>
			</Collapsible>

			<Dialog open={adding} onOpenChange={setAdding}>
				<DialogContent
					mobileVariant="fullscreen"
					hideClose="mobile"
					className={isMobile ? PHONE_DIALOG_CLASS : 'sm:max-w-lg'}
				>
					<DialogHeader className={isMobile ? PHONE_DIALOG_HEADER_CLASS : undefined}>
						{isMobile && (
							<BottomSheetTextButton className="justify-self-start" onClick={() => setAdding(false)}>
								Cancel
							</BottomSheetTextButton>
						)}
						<DialogTitle className={isMobile ? PHONE_DIALOG_TITLE_CLASS : undefined}>Add to sequence</DialogTitle>
						{isMobile && <span aria-hidden />}
					</DialogHeader>
					{adding && (
						<AddToReminderSequence
							customer={{ _id: customerId, name: _customer?.name }}
							source="conversation"
							stickyActions={isMobile}
							onAdded={() => {
								setAdding(false);
								setIsOpen(true);
							}}
							onCancel={() => setAdding(false)}
						/>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
};

export default ReminderSequences;
